export type TransferFilterTarget = {
  value: string;
  type: 'spog_agent' | 'teams_user' | 'teams_group';
  label: string;
  displayName: string;
  [key: string]: unknown;
};

/**
 * Chat transfers support online ESP agents only (no Teams users/groups, and
 * no offline agents — chat has no phone/voicemail fallback).
 * Voice transfers return the full list unchanged.
 */
export function filterTransferTargetsForChat<T extends TransferFilterTarget>(
  targets: T[],
  isChat: boolean
): T[] {
  if (!isChat) return targets;
  return targets.filter(
    target => target.type === 'spog_agent' && target.online === true
  );
}

/**
 * Decide whether a selected target must be transferred by phone (blind PSTN dial)
 * instead of the ESP SSE request flow. Teams targets always use phone; an ESP
 * agent uses phone only when it has a DID (targetPhoneNumber) — e.g. an offline
 * agent reachable only by their Teams phone.
 */
export function isPhoneTransfer(target: {
  type: 'spog_agent' | 'teams_user' | 'teams_group';
}): boolean {
  // ESP agents always transfer via SSE. Only Teams destinations dial by phone.
  if (target.type === 'spog_agent') return false;
  return true;
}

const TEAMS_ONLINE_AVAILABILITY = new Set(['Available', 'AvailableIdle']);

/**
 * Presence for a transfer target (drives the green/grey dot in dropdowns).
 * ESP agent: online flag from backend. Teams user/group: availability text.
 */
export function isTargetOnline(target: {
  type: 'spog_agent' | 'teams_user' | 'teams_group';
  online?: boolean;
  statusInfo?: string;
}): boolean {
  if (target.type === 'spog_agent') return target.online === true;
  return TEAMS_ONLINE_AVAILABILITY.has(target.statusInfo ?? '');
}
